
import { useCallback, useEffect, useState, useMemo, useContext } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
    OutlinedInput, TextField, styled, FormControl,
    InputLabel, Autocomplete, darken, lighten, createFilterOptions,
    Box,
    Chip,
    CardContent,
    Card,
} from '@mui/material';
import { Menu } from '@base-ui/react/menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CancelIcon from '@mui/icons-material/Cancel';
import { CustomButton } from "../custom-button";
import { fontbox, paddingX, paddingY, theme } from "../../emotion/utils";
import Grid2 from "@mui/material/Grid2";

import { css as emotion } from "./css";
import css from "./filters.module.css";
import FarmSelector from "./farmSelector";

export const getMonthDates = (monthIndex) => {
    const year = moment().year();
    return {
        startDate: moment().year(year).month(monthIndex).startOf('month').toDate(),
        endDate: moment().year(year).month(monthIndex).endOf('month').toDate(),
    };
};

export const getPresetDates = (preset) => {
    const now = moment();

    if (preset?.startsWith('month-')) {
        const monthIndex = parseInt(preset.split('-')[1], 10);
        return getMonthDates(monthIndex);
    }

    switch (preset) {
        case 'last7':
            return {
                startDate: now.clone().subtract(7, 'days').startOf('day').toDate(),
                endDate: now.clone().endOf('day').toDate(),
            };
        case 'currentQuarter':
            return {
                startDate: now.clone().startOf('quarter').toDate(),
                endDate: now.clone().endOf('quarter').toDate(),
            };
        case 'ytd':
            return {
                startDate: now.clone().startOf('year').toDate(),
                endDate: now.clone().endOf('day').toDate(),
            };
        default:
            return { startDate: null, endDate: null };
    }
};

const formatDateRange = (start, end) => {
    if (!start) return '';
    const s = moment(start);
    const e = end ? moment(end) : null;
    if (!e) return s.format('MMM D, YYYY');
    if (s.year() === e.year()) {
        return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
    }
    return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
};

const Filters = (props) => {

    const {
        farms,
        strategicAccounts,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        selectedFarms,
        setSelectedFarms,
        multiFarmSelect,
        preset,
        onPresetChange,
        children,
    } = props

    const [menuOpen, setMenuOpen] = useState(false);
    const [showCustomPicker, setShowCustomPicker] = useState(preset === 'custom');
    const [customPickerOpen, setCustomPickerOpen] = useState(false);

    const menuItemStyle = {
        padding: '10px 16px',
        cursor: 'pointer',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
    };

    const activeColor = '#1976d2';
    const isMonthPreset = preset?.startsWith('month-');

    const handlePresetSelect = (key) => {
        if (onPresetChange) onPresetChange(key);
        const { startDate: s, endDate: e } = getPresetDates(key);
        setStartDate(s);
        setEndDate(e);
        setShowCustomPicker(false);
        setMenuOpen(false);
    };

    const handleMonthSelect = (monthIndex) => {
        const { startDate: s, endDate: e } = getMonthDates(monthIndex);
        setStartDate(s);
        setEndDate(e);
        if (onPresetChange) onPresetChange(`month-${monthIndex}`);
        setShowCustomPicker(false);
        setMenuOpen(false);
    };

    const handleCustomSelect = () => {
        if (onPresetChange) onPresetChange('custom');
        setShowCustomPicker(true);
        setCustomPickerOpen(true);
        setMenuOpen(false);
    };

    const resetCustom = (e) => {
        e.stopPropagation();
        setShowCustomPicker(false);
        handlePresetSelect('ytd');
    };

    const onDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start ? moment(start).startOf('day').toDate() : null);
        setEndDate(end ? moment(end).endOf('day').toDate() : null);
        if (start && end) setCustomPickerOpen(false);
    };

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option) => option?.name + option?.parent_strategic_acct?.name + option?.parent_sub_entity_acct1?.name + option?.parent_sub__sub_entity_acct2?.name,
        trim: true
    })

    const GroupHeader = styled('div')(({ theme }) => ({
        position: 'sticky',
        top: '-8px',
        padding: '4px 10px',
        color: theme.palette.primary.main,
        backgroundColor: lighten(theme.palette.primary.light, 0.85),
        ...theme.applyStyles('dark', {
            backgroundColor: darken(theme.palette.primary.main, 0.8),
        }),
    }));

    const GroupItems = styled('ul')({
        padding: 0,
    });
    return (
        <>
        <style>{`
            [role="menu"] [data-highlighted] {
                background-color: rgba(0, 0, 0, 0.04);
            }
            [role="menu"] [data-popup-open] {
                background-color: rgba(0, 0, 0, 0.04);
            }
        `}</style>
        <div
          css={emotion.filters}
          className={css.filters}
        >
            <>
                <FormControl sx={{ backgroundColor: "#fff", minWidth: 230 }}>
                    <InputLabel sx={{ backgroundColor: "#fff" }} shrink={true} id="inv-label">Invoice Created</InputLabel>
                    {showCustomPicker ? (
                        <DatePicker
                            customInput={
                                <OutlinedInput
                                    sx={{ width: '100%' }}
                                    label="Invoice Created"
                                    endAdornment={
                                        <CancelIcon
                                            sx={{ cursor: 'pointer', fontSize: 22, color: 'rgba(0, 0, 0, 0.26)', '&:hover': { color: 'rgba(0, 0, 0, 0.4)' } }}
                                            onClick={resetCustom}
                                        />
                                    }
                                />
                            }
                            portalId="root-portal"
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={onDateChange}
                            showYearDropdown
                            open={customPickerOpen}
                            onInputClick={() => setCustomPickerOpen(true)}
                            onClickOutside={() => setCustomPickerOpen(false)}
                        />
                    ) : (
                        <Menu.Root open={menuOpen} onOpenChange={setMenuOpen}>
                            <Menu.Trigger
                                render={<OutlinedInput
                                    label="Invoice Created"
                                    readOnly
                                    value={formatDateRange(startDate, endDate)}
                                    sx={{ cursor: 'pointer', '& input': { textAlign: 'left' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87)', borderWidth: 1 } }}
                                />}
                            />
                            <Menu.Portal>
                                <Menu.Positioner style={{ zIndex: 1300 }} side="bottom" align="start" sideOffset={4} alignOffset={0}>
                                    <Menu.Popup style={{ background: '#fff', borderRadius: 4, boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)', padding: '8px 0', minWidth: 180 }}>
                                        <Menu.Item style={{ ...menuItemStyle, color: preset === 'last7' ? activeColor : undefined }} onClick={() => handlePresetSelect('last7')}>Last 7 Days</Menu.Item>

                                        <Menu.SubmenuRoot>
                                            <Menu.SubmenuTrigger openOnHover delay={150} style={{ ...menuItemStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: isMonthPreset ? activeColor : undefined }}>
                                                Month
                                                <ChevronRightIcon fontSize="small" style={{ marginLeft: 16 }} />
                                            </Menu.SubmenuTrigger>
                                            <Menu.Portal>
                                                <Menu.Positioner style={{ zIndex: 1350 }} side="right" align="start" sideOffset={0}>
                                                    <Menu.Popup style={{ background: '#fff', borderRadius: 4, boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)', padding: '8px 0', minWidth: 140 }}>
                                                        {moment.months().map((name, i) => (
                                                            <Menu.Item key={i} style={{ ...menuItemStyle, color: preset === `month-${i}` ? activeColor : undefined }} onClick={() => handleMonthSelect(i)}>{name}</Menu.Item>
                                                        ))}
                                                    </Menu.Popup>
                                                </Menu.Positioner>
                                            </Menu.Portal>
                                        </Menu.SubmenuRoot>

                                        <Menu.Item style={{ ...menuItemStyle, color: preset === 'currentQuarter' ? activeColor : undefined }} onClick={() => handlePresetSelect('currentQuarter')}>Quarter</Menu.Item>
                                        <Menu.Item style={{ ...menuItemStyle, color: preset === 'ytd' ? activeColor : undefined }} onClick={() => handlePresetSelect('ytd')}>Year to Date</Menu.Item>
                                        <Menu.Item style={{ ...menuItemStyle, color: preset === 'custom' ? activeColor : undefined }} onClick={handleCustomSelect}>Custom Range</Menu.Item>
                                    </Menu.Popup>
                                </Menu.Positioner>
                            </Menu.Portal>
                        </Menu.Root>
                    )}
                </FormControl>

                <FarmSelector
                    farms={farms}
                    strategicAccounts={strategicAccounts}
                    selectedFarms={selectedFarms}
                    setSelectedFarms={setSelectedFarms}
                    multiFarmSelect={multiFarmSelect}
                />

                
            </>

            {children && (
                <div style={{ marginLeft: "auto", alignSelf: "center" }}>
                    {children}
                </div>
            )}

        </div>
        </>
    )

}
export default Filters;
